import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {Badge, Col, Form, FormGroup, FormLabel, ListGroupItem} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import * as yup from 'yup'

import { IRegistration } from '../../../store/registrations/interfaces'
import registrations, { updateRegistration } from '../../../store/registrations/registrations'
import { PriceCategory as TPriceCategory } from '../../../store/events/interfaces'
import { AddValidatorFunc } from '../../../hooks/validators'
import { RootState } from '../../../store'
import { formatPrice } from '../../../utils'

const PriceCategory = (props: {
  registration: IRegistration
  categories: TPriceCategory[]
  event_price: number
  editable: boolean
  setError: (prevState: boolean) => void
  addValidator?: AddValidatorFunc
}) => {
  const dispatch = useDispatch()
  const {
    registration,
    categories,
    editable,
    event_price,
    addValidator,
  } = props
  const { loadedViaCode } = useSelector((state: RootState) => state.misc)
  const { originalRegistrationIds } = useSelector(
    (state: RootState) => state.participant.data,
  )

  const isOriginalReg = originalRegistrationIds?.includes(
    registration.event.event_id,
  )
  const { register, trigger, getValues, formState: { errors } } = useForm<{
    priceCategory: string
  }>({
    mode: 'onChange',
    defaultValues: {
      priceCategory: registration.priceCategory,
    },
    resolver: yupResolver(yup.object().shape({
      priceCategory: yup
        .string()
        .oneOf(
          ['', ...categories.map(cat => cat.name)],
          'Bitte eine der angegebenen Preiskategorien auswählen.',
        ),
    })),
  })
  const lastError = useRef(errors.priceCategory)

  useEffect(
    () => {
      if (lastError.current === errors.priceCategory) {
        if (editable && addValidator) {
          addValidator(`priceCategory-${registration.event.event_id}`, { trigger, formName: 'PriceCategory' })
        }
      } else {
        if (errors.priceCategory) {
          props.setError(true)
          lastError.current = errors.priceCategory
        } else {
          props.setError(false)
          lastError.current = errors.priceCategory
        }
      }
    },
    [register, errors.priceCategory])

  return (
    <ListGroupItem className="m-0">
      <FormGroup>
        <FormLabel>Preiskategorie</FormLabel>
        <Form.Control
          {...register('priceCategory', {
            onChange: () => {
              dispatch(
                updateRegistration({
                  eventId: registration.event.event_id,
                  ...getValues(),
                })
              )
            },
          })}
          id={`priceCategory-${registration.event.event_id}`}
          as="select"
          className="custom-select"
          disabled={!editable || (loadedViaCode && isOriginalReg)}
          isInvalid={!!errors.priceCategory}
          value={registration.priceCategory}
        >
          {!(event_price === 0 && registration.event.hide_price) && (
            <option value="">
              Normalpreis{' '}
              {event_price !== undefined
                ? event_price === 0
                  ? 'Kostenlos'
                  : `(${formatPrice(event_price)})`
                : 'Kostenlos'}
            </option>
          )}
          {event_price === 0 && registration.event.hide_price && (
            <option value="NONE">Bitte Preiskategorie auswählen</option>
          )}
          {categories.map((category, index) => (
            <option key={index} value={category.name}>
              {category.name} ({formatPrice(category.price)})
            </option>
          ))}
        </Form.Control>
        <Form.Control.Feedback type="invalid">
          {errors.priceCategory && errors.priceCategory.message}
        </Form.Control.Feedback>
        {registration.event.has_external_payment && (
          <Col className="pl-0 mt-2">
            <Badge variant="warning" tabIndex={0}>
              Achtung
            </Badge>{' '}
            <small className="text-muted">
              Bezahlung wird direkt beim Veranstalter geregelt!
            </small>
          </Col>
        )}
      </FormGroup>
    </ListGroupItem>
  )
}

export default PriceCategory
